import { DATE_PRESETS, SORT_FIELDS, SORT_DIRECTIONS, DATE_PRESET_LABELS } from "./constants";
import { getNodesForStages } from "./workflowUtils";

function parseDate(dateString) {
  if (!dateString) return null;
  const normalized = dateString.includes("T") ? dateString : dateString.replace(" ", "T");
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfDay(date) {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function endOfDay(date) {
  const copy = new Date(date);
  copy.setHours(23, 59, 59, 999);
  return copy;
}

export function getDateRange(preset, customRange) {
  const now = new Date();

  switch (preset) {
    case DATE_PRESETS.TODAY:
      return { start: startOfDay(now), end: endOfDay(now) };

    case DATE_PRESETS.YESTERDAY: {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
    }

    case DATE_PRESETS.LAST_7_DAYS: {
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
      return { start: startOfDay(sevenDaysAgo), end: endOfDay(now) };
    }

    case DATE_PRESETS.LAST_30_DAYS: {
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29);
      return { start: startOfDay(thirtyDaysAgo), end: endOfDay(now) };
    }

    case DATE_PRESETS.THIS_MONTH: {
      const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: startOfDay(firstOfMonth), end: endOfDay(now) };
    }

    case DATE_PRESETS.CUSTOM_RANGE: {
      if (!customRange?.startDate || !customRange?.endDate) return null;
      const start = parseDate(customRange.startDate);
      const end = parseDate(customRange.endDate);
      if (!start || !end) return null;
      return { start: startOfDay(start), end: endOfDay(end) };
    }

    default:
      return null;
  }
}

function isWithinDateFilter(dateString, dateFilterValue) {
  if (!dateFilterValue?.preset) return true;

  const parsedDate = parseDate(dateString);
  if (!parsedDate) return false;

  const range = getDateRange(dateFilterValue.preset, dateFilterValue.customRange);
  if (!range) return true;

  return parsedDate >= range.start && parsedDate <= range.end;
}

function passesArrayFilter(selectedIds, leadValue) {
  if (selectedIds.length === 0) return true;

  const includeUnassigned = selectedIds.includes("unassigned");
  if ((leadValue === null || leadValue === undefined) && includeUnassigned) {
    return true;
  }

  return selectedIds.includes(leadValue);
}

export function applySearch(leads, search) {
  if (!search || !search.trim()) return leads;

  const query = search.trim().toLowerCase();
  return leads.filter((lead) => {
    const searchableFields = [
      lead.name,
      lead.email,
      lead.phone,
      lead.company,
      lead.source,
    ];

    return searchableFields.some((field) =>
      String(field || "").toLowerCase().includes(query)
    );
  });
}

export function applyFilters(leads, filters) {
  if (!filters) return leads;

  const selectedLeadIds = new Set(
    (filters.leadIds || []).map((leadId) => String(leadId))
  );
  const hasQueueFilter = Boolean(filters.queueKey);

  return leads.filter((lead) => {
    const stageId = Number(
      lead.lead_stage_id ?? lead.stage?.id ?? lead.current_state?.current_stage_id
    );
    const nodeId = Number(lead.current_state?.current_node_id);

    return (
      (!hasQueueFilter || selectedLeadIds.has(String(lead.id))) &&
      passesArrayFilter(filters.stageIds, stageId) &&
      passesArrayFilter(filters.nodeIds, nodeId) &&
      passesArrayFilter(filters.assignedTo, lead.assigned_user_id) &&
      isWithinDateFilter(lead.created_at, filters.createdDate) &&
      isWithinDateFilter(lead.updated_at, filters.updatedDate) &&
      isWithinDateFilter(lead.stage_changed_at, filters.stageUpdated)
    );
  });
}

function compareIds(leadA, leadB) {
  return Number(leadA.id) - Number(leadB.id);
}

function compareByDateField(field) {
  return (leadA, leadB) => {
    const dateA = parseDate(leadA[field]);
    const dateB = parseDate(leadB[field]);
    return (dateA?.getTime() || 0) - (dateB?.getTime() || 0);
  };
}

function compareByTextField(field) {
  return (leadA, leadB) =>
    String(leadA[field] || "").localeCompare(String(leadB[field] || ""), undefined, {
      sensitivity: "base",
    });
}

const SORT_COMPARATORS = {
  [SORT_FIELDS.ID]: compareIds,
  [SORT_FIELDS.LEAD_NAME]: compareByTextField("name"),
  [SORT_FIELDS.STAGE_UPDATED_AT]: compareByDateField("stage_changed_at"),
  [SORT_FIELDS.UPDATED_AT]: compareByDateField("updated_at"),
  [SORT_FIELDS.CREATED_AT]: compareByDateField("created_at"),
};

export function applySorting(leads, sort) {
  if (!sort?.field) return leads;

  const compare = SORT_COMPARATORS[sort.field] || SORT_COMPARATORS[SORT_FIELDS.CREATED_AT];

  return [...leads].sort((leadA, leadB) => {
    const comparison = compare(leadA, leadB);
    return sort.direction === SORT_DIRECTIONS.ASC ? comparison : -comparison;
  });
}

export function buildLeadQuery(filters, search, sort) {
  const params = {};

  if (search?.trim()) params.search = search.trim();
  if (filters?.stageIds?.length) params.stage_ids = filters.stageIds;
  if (filters?.nodeIds?.length) params.node_ids = filters.nodeIds;
  if (filters?.assignedTo?.length) params.assigned_to = filters.assignedTo;
  if (filters?.leadIds?.length) params.lead_ids = filters.leadIds;
  if (filters?.customFields && Object.keys(filters.customFields).length) {
    const custom = Object.fromEntries(Object.entries(filters.customFields).filter(([key]) => !key.startsWith("starter:")));
    const starter = Object.fromEntries(Object.entries(filters.customFields).filter(([key]) => key.startsWith("starter:")).map(([key, value]) => [key.slice(8), value]));
    if (Object.keys(custom).length) params.custom_filters = JSON.stringify(custom);
    if (Object.keys(starter).length) params.starter_filters = JSON.stringify(starter);
  }

  const dateFilters = [
    ["created", filters?.createdDate],
    ["updated", filters?.updatedDate],
    ["stage_changed", filters?.stageUpdated],
  ];

  dateFilters.forEach(([prefix, value]) => {
    if (!value) return;
    const range = getDateRange(value.preset, value.customRange);
    if (!range) return;
    params[`${prefix}_from`] = range.start.toISOString();
    params[`${prefix}_to`] = range.end.toISOString();
  });

  if (sort?.field) {
    if (sort.field.startsWith("starter:")) {
      params.starter_sort = sort.field.slice(8);
    } else if (sort.field.startsWith("custom:")) {
      params.custom_sort = sort.field.slice(7);
    } else {
      params.sort_by = sort.field === SORT_FIELDS.STAGE_UPDATED_AT ? "stage_changed_at" : sort.field;
    }
    params.sort_direction = sort.direction || SORT_DIRECTIONS.DESC;
  }

  return params;
}

/* Counts how many filters are currently active. */
export function countActiveFilters(filters) {
  if (!filters) return 0;

  const activeFlags = [
    filters.stageIds.length > 0,
    filters.assignedTo.length > 0,
    (filters.leadIds || []).length > 0 || Boolean(filters.queueKey),
    Boolean(filters.createdDate),
    Boolean(filters.updatedDate),
    Boolean(filters.stageUpdated),
    Object.keys(filters.customFields || {}).length > 0,
  ];

  return activeFlags.filter(Boolean).length;
}

function formatDateFilterLabel(dateFilterValue) {
  if (!dateFilterValue?.preset) return "";

  if (dateFilterValue.preset === DATE_PRESETS.CUSTOM_RANGE && dateFilterValue.customRange) {
    const { startDate, endDate } = dateFilterValue.customRange;
    return `${startDate} - ${endDate}`;
  }

  return DATE_PRESET_LABELS[dateFilterValue.preset] || dateFilterValue.preset;
}

function buildStageChips(stageIds, selectedNodeIds, stages, workflowStages) {
  const stageById = new Map(stages.map((stage) => [stage.id, stage]));

  return stageIds.map((stageId) => {
    const stage = stageById.get(stageId);
    const stageNodes = getNodesForStages(workflowStages, [stageId]);
    const selectedNodeCount = stageNodes.filter((node) => selectedNodeIds.includes(node.id)).length;

    return {
      type: `stage-${stageId}`,
      filterType: "stage",
      stageId,
      label: selectedNodeCount > 0 ? `${stage?.name} (${selectedNodeCount} nodes)` : stage?.name,
    };
  });
}

function buildAssignedToChips(userIds, users) {
  const userById = new Map(users.map((user) => [user.user_id, user]));

  return userIds.map((userId) => ({
    type: `assigned-${userId}`,
    filterType: "assignedTo",
    userId,
    label:
      userId === "unassigned"
        ? "Unassigned"
        : userById.get(userId)?.user?.name,
  }));
}

export function buildFilterChipData(filters, lookups = {}) {
  const { stages = [], users = [], workflowStages = [], customFields = [] } = lookups;
  const chips = [];

  if (filters.stageIds?.length) {
    chips.push(...buildStageChips(filters.stageIds, filters.nodeIds, stages, workflowStages));
  }

  if (filters.assignedTo?.length) {
    chips.push(...buildAssignedToChips(filters.assignedTo, users));
  }

  if (filters.queueKey) {
    chips.push({
      type: `queue-${filters.queueKey}`,
      filterType: "queue",
      label: filters.queueLabel || "CRM queue",
    });
  }

  if (filters.createdDate) {
    chips.push({ type: "createdDate", filterType: "createdDate", label: `Created: ${formatDateFilterLabel(filters.createdDate)}` });
  }

  if (filters.updatedDate) {
    chips.push({ type: "updatedDate", filterType: "updatedDate", label: `Updated: ${formatDateFilterLabel(filters.updatedDate)}` });
  }

  if (filters.stageUpdated) {
    chips.push({ type: "stageUpdated", filterType: "stageUpdated", label: `Stage Updated: ${formatDateFilterLabel(filters.stageUpdated)}` });
  }

  Object.entries(filters.customFields || {}).forEach(([key, condition]) => {
    const field = customFields.find((item) => item.key === key);
    const operator = condition?.operator || "eq";
    const rawValue = condition?.value;
    const value = Array.isArray(rawValue) ? rawValue.join(", ") : rawValue;
    chips.push({
      type: `custom-${key}`,
      filterType: "custom",
      fieldKey: key,
      label: `${field?.label || key}: ${["empty", "not_empty"].includes(operator) ? operator.replace("_", " ") : value}`,
    });
  });

  return chips;
}
